import { createProPlugin, inputs } from "@formkit/pro";
import type { DefaultConfigOptions } from "@formkit/vue";
import { createInput } from "@formkit/vue";
import { createMultiStepPlugin } from "@formkit/addons";
import type { FormKitValidationRule } from "@formkit/validation";
import { nl } from "@formkit/i18n";
import type { FormKitInputs } from "@formkit/inputs";
import { removableRadio } from "@/components/input-formkit/removableRadio";
import { optionalGroup } from "@/components/input-formkit/optionalGroup";
import { iban } from "@/components/input-formkit/iban";
import FormkitOptionInput from "./components/input-formkit/FormkitOptionInput.vue";
import type { AvailableOptionTypes } from "./composables/useOptionsToArray";
import FormkitNumeric from "./components/input-formkit/FormkitNumeric.vue";
import FormkitDate from "./components/input-formkit/FormkitDate.vue";

declare module "@formkit/inputs" {
	interface FormKitInputProps<Props extends FormKitInputs<Props>> {
		optionalGroup: {
			type: "optionalGroup";
		};
		removableRadio: {
			type: "removableRadio";
		};
		loanlinkDate: {
			type: "loanlinkDate";
			twoDigitYearCentury?: "past" | "future";
		};
		loanlinkCurrency: {
			type: "loanlinkCurrency";
		};
		loanlinkPercentage: {
			type: "loanlinkPercentage";
		};
		loanlinkNumeric: {
			type: "loanlinkNumeric";
		};
		loanlinkOption: {
			type: "loanlinkOption";
			options: AvailableOptionTypes;
		};
	}
}

const formkitPro = createProPlugin(import.meta.env.VITE_FORMKIT_KEY, inputs);

const integer: FormKitValidationRule = (node) =>
	Number.isInteger(Number(node.value));

const maxExclusive: FormKitValidationRule = (node, max) => {
	const value = Number(node.value);
	const limit = Number(max);
	return Number.isFinite(value) && Number.isFinite(limit) && value < limit;
};

const minExclusive: FormKitValidationRule = (node, min) => {
	const value = Number(node.value);
	const limit = Number(min);
	return Number.isFinite(value) && Number.isFinite(limit) && value > limit;
};

export const formkitConfig: DefaultConfigOptions = {
	plugins: [formkitPro, createMultiStepPlugin()],
	locales: { nl },
	locale: "nl",
	rules: { integer, minExclusive, maxExclusive, iban },
	inputs: {
		removableRadio,
		optionalGroup,
		loanlinkDate: createInput(FormkitDate, {
			props: ["twoDigitYearCentury"],
		}),
		loanlinkCurrency: createInput(FormkitNumeric),
		loanlinkPercentage: createInput(FormkitNumeric),
		loanlinkNumeric: createInput(FormkitNumeric),
		loanlinkOption: createInput(FormkitOptionInput, {
			type: "input",
			props: ["options"],
		}),
	},
	config: {
		// TODO: adopt the application to use global formkit config
		// to do this add theme rootclasses here
	},
	messages: {
		en: {
			validation: {
				integer: ({ name }) => {
					return `${name} must be a whole number.`;
				},
				maxExclusive: ({ name, args }) => {
					return `${name} must be smaller than ${args[0]}.`;
				},
				minExclusive: ({ name, args }) => {
					return `${name} must be greater than ${args[0]}.`;
				},
				matches: ({ name, args }) => {
					return `${name} must satisfy the following regular expression: ${args[0]}`;
				},
			},
		},
		nl: {
			validation: {
				integer: ({ name }) => {
					return `${name} moet een heel getal zijn.`;
				},
				maxExclusive: ({ name, args }) => {
					return `${name} moet kleiner dan ${args[0]} zijn.`;
				},
				minExclusive: ({ name, args }) => {
					return `${name} moet groter dan ${args[0]} zijn.`;
				},
				matches: ({ name, args }) => {
					return `${name} moet voldoen aan de volgende reguliere expressie: ${args[0]}`;
				},
			},
		},
	},
};
